import { Button } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { StatusResultButton, StatusResultButtonButtonAction } from '@cash-web/protos/squareup/franklin/status_result.pb'
import logger from '@cash-web/shared/util-logger'
import { useLoadUrl } from '@cash-web/shared-data-access-load-url'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { WebInteractClick } from '@squareup/customer-data-events-web'

export const BlockerStatusResultButton = ({
  isPrimary,
  button,
  onClose,
}: {
  isPrimary: boolean
  button?: StatusResultButton
  onClose?: () => void
}) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const flow = useFlowContext()
  const loadUrl = useLoadUrl()

  if (button == null) {
    return null
  }

  const handleButtonAction = (button: StatusResultButton) => {
    logger.addBreadcrumb('BlockerStatusResultButton', { button })
    logCdfEvent(
      WebInteractClick({
        componentName: 'BlockerStatusResultButton',
        componentType: `ButtonAction[${button.action}]`,
      })
    )

    switch (button.action) {
      case StatusResultButtonButtonAction.OPEN_URL: {
        if (button?.url == null) {
          break
        }
        // loadUrl will handle all route types including logging unsupported routes
        loadUrl({ url: button.url })
        break
      }
      // NOTE: We will want to support ADD_CASH once we support the ability to add cash on Web.
      case StatusResultButtonButtonAction.PAY_SCREEN:
      case StatusResultButtonButtonAction.ADD_CASH: {
        onClose?.()
        break
      }
      case StatusResultButtonButtonAction.START_SUPPORT_FLOW: {
        // We need to support this button action: https://block.atlassian.net/browse/FSWEB-11150
        onClose?.()
        break
      }
      case StatusResultButtonButtonAction.LINK_CARD: {
        // We need to support this button action: https://block.atlassian.net/browse/FSWEB-11149
        onClose?.()
        break
      }
      default: {
        logger.error(new Error(`Unimplemented StatusResultButtonButtonAction: ${button.action}`))
        // This action has not been implemented yet so we don't know what to do. We'll just close the modal.
        onClose?.()
        break
      }
    }
  }

  const handleButtonBlockerAction = (blockerAction: BlockerAction, buttonText?: string) => {
    logCdfEvent(
      WebInteractClick({
        componentName: 'BlockerStatusResultButton',
        componentType: `ButtonAction[BLOCKER_ACTION:${blockerAction}]`,
      })
    )
    logger.addBreadcrumb('BlockerStatusResultButton', { blockerAction })

    flow.executeBlockerAction(blockerAction)
  }

  const handleClick = () => {
    if (button?.blocker_action) {
      handleButtonBlockerAction(button.blocker_action, button.text)
    } else if (button?.action) {
      handleButtonAction(button)
    } else {
      logger.error(new Error(`StatusResultButton is missing action Text[${button?.text}]`))
    }
  }

  return (
    <Button size="large" variant={isPrimary ? 'primary' : 'secondary'} onClick={handleClick}>
      {button.text}
    </Button>
  )
}
